import { useAuthDetails } from "@/context/auth-context";
import { SystemRole } from "@/data/use-users-data";
import { GroupRole } from "@/lib/consts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const ROLE_OPTIONS: Partial<Record<SystemRole, string>> = {
  user: "User",
  super: "Super Admin",
  bot: "Bot",
};

const ROLE_CREATION_PERMISSION: Partial<Record<SystemRole, SystemRole[]>> = {
  super: ["super", "user", "bot"],
  user: ["user"],
  bot: [],
};

const ORGANIZATION_ROLE_OPTIONS = {
  admin: "Admin",
  member: "Member",
} as const;

type OrganizationRole = keyof typeof ORGANIZATION_ROLE_OPTIONS;

const GROUP_ROLE_OPTIONS: Record<GroupRole, string> = {
  admin: "Admin",
  creator: "Creator",
  member: "Member",
};

type SystemRoleSelectionProps = {
  value: SystemRole | undefined;
  onChange: (role: SystemRole) => void;
  disabled?: boolean;
  id?: string;
  disableRoleOption?: SystemRole[];
} & React.ComponentPropsWithoutRef<typeof Select>;

export function UserRoleSelection({
  value,
  onChange,
  disabled,
  id,
  disableRoleOption,
  ...props
}: SystemRoleSelectionProps) {
  const authDetails = useAuthDetails();
  if (!authDetails) return null;

  const allowedRoles = ROLE_CREATION_PERMISSION[authDetails.role] || [];

  return (
    <Select
      {...props}
      value={value}
      onValueChange={onChange}
      disabled={disabled}
    >
      <SelectTrigger id={id} className="w-full">
        <SelectValue placeholder="Select role" />
      </SelectTrigger>
      <SelectContent>
        {allowedRoles.map((label) => (
          <SelectItem
            key={label}
            value={label}
            disabled={disableRoleOption?.includes(label)}
          >
            {ROLE_OPTIONS[label]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

type GroupRoleSelectionProps = {
  value: GroupRole | undefined;
  onChange: (role: GroupRole) => void;
  disabled?: boolean;
  id?: string;
  disableRoleOption?: GroupRole[];
} & React.ComponentPropsWithoutRef<typeof Select>;

export function OrganizationRoleSelection({
  value,
  onChange,
  disabled,
}: {
  value: OrganizationRole;
  onChange: (role: OrganizationRole) => void;
  disabled?: boolean;
}) {
  return (
    <Select
      value={value}
      onValueChange={(role) => onChange(role as OrganizationRole)}
      disabled={disabled}
    >
      <SelectTrigger className="w-full" aria-label="Organization Role">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {Object.entries(ORGANIZATION_ROLE_OPTIONS).map(([role, label]) => (
          <SelectItem key={role} value={role}>
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function GroupRoleSelection({
  value,
  onChange,
  disabled,
  id,
  disableRoleOption,
  ...props
}: GroupRoleSelectionProps) {
  const roles: GroupRole[] = ["admin", "creator", "member"];

  return (
    <Select
      {...props}
      value={value}
      onValueChange={onChange}
      disabled={disabled}
    >
      <SelectTrigger id={id} className="w-full" aria-label="Group Role">
        <SelectValue placeholder="Select role" />
      </SelectTrigger>
      <SelectContent>
        {roles.map((role) => (
          <SelectItem
            key={role}
            value={role}
            disabled={disableRoleOption?.includes(role)}
          >
            {GROUP_ROLE_OPTIONS[role]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
